import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiShoppingCart, FiHeart } from 'react-icons/fi';
import { addToCart } from '../lib/cart';
import toast from 'react-hot-toast';
import { useCurrency } from '../context/CurrencyContext';

export default function Compare() {
  const { formatPrice } = useCurrency();
  const [compareItems, setCompareItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load compare items from localStorage
    const savedCompare = localStorage.getItem('compare');
    if (savedCompare) {
      setCompareItems(JSON.parse(savedCompare));
    }
    setLoading(false);
  }, []);

  const removeFromCompare = (productId) => {
    const updatedCompare = compareItems.filter(item => item.id !== productId);
    setCompareItems(updatedCompare);
    localStorage.setItem('compare', JSON.stringify(updatedCompare));
    toast.success('Removed from comparison');
  };

  const addToCartFromCompare = (product) => {
    addToCart(product, 1);
    toast.success(`${product.name} added to cart`);
    window.dispatchEvent(new Event('cartUpdated'));
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto"></div>
            <p className="text-white mt-4">Loading comparison...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">Product Comparison</h1>
          <p className="text-gray-400">
            Compare {compareItems.length} {compareItems.length === 1 ? 'product' : 'products'}
          </p>
        </div>

        {compareItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto text-gray-600 mb-4" style={{ fontSize: '64px' }}>⚖️</div>
            <h2 className="text-2xl font-bold text-white mb-4">No products to compare</h2>
            <p className="text-gray-400 mb-8">
              Add products to comparison by clicking the compare icon on any product.
            </p>
            <Link
              to="/collections"
              className="inline-flex items-center px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {/* Product Images and Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {compareItems.map((product) => (
                  <div
                    key={product.id}
                    className="bg-gray-900 rounded-2xl overflow-hidden relative shadow-2xl border border-gray-800"
                  >
                    <button
                      onClick={() => removeFromCompare(product.id)}
                      className="absolute top-4 right-4 p-2 bg-red-500/90 backdrop-blur-sm text-white rounded-full hover:bg-red-600 transition-colors z-10"
                      title="Remove from comparison"
                    >
                      <FiX size={16} />
                    </button>

                    <Link to={`/products/${product.slug || product.id}`} className="block">
                      <div className="relative h-64">
                        <img
                          src={product.images?.[0]?.src || '/placeholder-image.jpg'}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onLoad={(e) => {
                            e.target.style.opacity = '1';
                          }}
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                          style={{ opacity: 0, transition: 'opacity 0.3s ease-in-out' }}
                        />
                      </div>
                    </Link>

                    <div className="p-6">
                      <Link to={`/products/${product.slug || product.id}`}>
                        <h3 className="text-white text-lg font-semibold mb-3 line-clamp-2 hover:text-pink-500 transition-colors">
                          {product.name}
                        </h3>
                      </Link>

                      <div className="flex items-center space-x-3 mb-4">
                        <span className="text-pink-500 font-bold text-xl">
                          {formatPrice(product.sale_price || product.price)}
                        </span>
                        {product.sale_price && product.sale_price < product.regular_price && (
                          <span className="text-gray-400 line-through text-lg">
                            {formatPrice(product.regular_price)}
                          </span>
                        )}
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => addToCartFromCompare(product)}
                          className="flex-1 px-4 py-2 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors flex items-center justify-center"
                        >
                          <FiShoppingCart className="mr-2" size={16} />
                          Add to Cart
                        </button>
                        <Link
                          to={`/products/${product.slug || product.id}`}
                          className="px-4 py-2 border-2 border-gray-600 text-white rounded-lg hover:border-pink-500 transition-colors flex items-center justify-center"
                        >
                          <FiHeart size={16} />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Detailed Comparison Table */}
              <div className="bg-gray-900 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-6 py-4 text-left text-white font-semibold">Features</th>
                        {compareItems.map((product) => (
                          <th key={product.id} className="px-6 py-4 text-center text-white font-semibold min-w-[200px]">
                            {product.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      <tr>
                        <td className="px-6 py-4 text-gray-300 font-medium">Price</td>
                        {compareItems.map((product) => (
                          <td key={product.id} className="px-6 py-4 text-center text-pink-500 font-bold">
                            {formatPrice(product.sale_price || product.price)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-gray-300 font-medium">SKU</td>
                        {compareItems.map((product) => (
                          <td key={product.id} className="px-6 py-4 text-center text-gray-400">
                            {product.sku || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-gray-300 font-medium">Stock Status</td>
                        {compareItems.map((product) => (
                          <td key={product.id} className="px-6 py-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${product.stock_status === 'instock'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                              }`}>
                              {product.stock_status === 'instock' ? 'In Stock' : 'Out of Stock'}
                            </span>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-gray-300 font-medium">Weight</td>
                        {compareItems.map((product) => (
                          <td key={product.id} className="px-6 py-4 text-center text-gray-400">
                            {product.weight || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-gray-300 font-medium">Dimensions</td>
                        {compareItems.map((product) => (
                          <td key={product.id} className="px-6 py-4 text-center text-gray-400">
                            {product.dimensions ?
                              `${product.dimensions.length} × ${product.dimensions.width} × ${product.dimensions.height}` :
                              'N/A'
                            }
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
